/**
 * services/drawings.js — Client for the engineering-drawing KB feature
 * and the Design Studio generative endpoint.
 *
 * See /app/backend/drawings_router.py for the server surface.
 */
import axios from 'axios';
import { usePortalStore } from '../stores';

const authClient = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  timeout: 120000,   // vision extraction can legitimately take 40 s
});
authClient.interceptors.request.use((cfg) => {
  const tok = usePortalStore.getState().token;
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});

export const drawingsApi = {
  list: (kbId) => authClient.get(`/portal/kb/${kbId}/drawings`),
  get: (id) => authClient.get(`/portal/kb/drawings/${id}`),
  remove: (id) => authClient.delete(`/portal/kb/drawings/${id}`),
  upload: (kbId, file, onProgress, opts = {}) => {
    const form = new FormData();
    form.append('file', file);
    return authClient.post(`/portal/kb/${kbId}/drawings/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal: opts.signal,
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
  },
  thumbUrl: (id) => `${process.env.REACT_APP_BACKEND_URL}/api/portal/kb/drawings/${id}/thumb`,
};

export const designApi = {
  generate: (body) => authClient.post('/portal/design/generate', body),
  listGenerations: () => authClient.get('/portal/design/generations'),
  getGeneration: (id) => authClient.get(`/portal/design/generations/${id}`),
  rasterUrl: (id) => `${process.env.REACT_APP_BACKEND_URL}/api/portal/design/generations/${id}/raster`,
  publish: (id) => authClient.post(`/portal/design/${id}/publish`),
  unpublish: (id) => authClient.post(`/portal/design/${id}/unpublish`),
  publicUrl: (token) => `${window.location.origin}/public/design/${token}`,
  // Unauthenticated fetch — used by the public share page.
  getPublic: (token) => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/design/${token}`),
  // SSE URL for regenerating a single sheet — the caller uses fetch()
  // (not axios) so it can stream the response body.
  regenerateSheetUrl: (id, sheetIdx) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/portal/design/generations/${id}/sheets/${sheetIdx}/regenerate`,
};
